import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ErrorIcon from "../../Icons/NotificationMessage/Info";
import { sendNotification } from "../../redux/actions/NotificationAction";
import {
  removeModalNotification,
  sendModalNotification,
} from "../../redux/actions/ModalNotificationAction";

import InitialConfigurationApi from "../../api/InitialConfigurationApi";
import WindowsServiceApi from "../../api/WindowsServiceApi";

import LayoutContent from "../LayoutContent";
import Header from "../Header/Header";
import MtiButton from "../Buttons/MtiButton";
import SelectField from "./SelectField";
import EulaModal from "./EulaModal";
import { InputField, EInputState } from "./InputField";

import {
  ECanProduct,
  canProductDropdown,
  integrationConnectionTypeDropdown,
  EDatabaseType,
  EImagerType,
  ENotificationType,
  EInstrumentType,
  instrumentTypeStrings,
  databaseTypeStrings,
  useWindowsAuthenticationTypeDropdown,
  UseWindowsAuthenticationType,
} from "../../Model";
import { InstrumentsInfo } from "../../Model/InstrumentsInfo";
import { SuiteInitialConfiguration } from "../../Model/InitialConfiguration";

import "./InitialConfiguration.css";
import { IconEdit, IconReset } from "../../assets/icons";
import {
  ETemperatureControlType,
  temperatureControlTypeDropdown,
} from "../../Model/ETemperatureControlType";
import { EIntegrationConnectionType } from "../../Model/EIntegrationConnectionType";

const initConfiguration: SuiteInitialConfiguration = {
  suiteDatabase: {
    serverName: "",
    databaseName: "",
    userID: "",
    password: "",
    useWindowsAuthentication: false,
    checkVersion: false,
    maxPoolSize: 0,
  },
  brokerUrl: "",
  databaseType: EDatabaseType.Unknown,
  limsInstrumentId: 0,
  instrumentId: "",
  instrumentName: "",
  instrumentType: EInstrumentType.Unknown,
  imagerType: EImagerType.Unknown,
  canProductType: ECanProduct.UNKNOWN,
  temperatureControlType: ETemperatureControlType.Unknown,
  listenUrl: "",
  connectionType: EIntegrationConnectionType.FormulatrixIntegrationService,
  restApi: {
    apiUrl: "",
    integrationUrl: "",
    apiVersion: "",
    username: "",
    password: "",
    useLog: false,
    timeOutInSeconds: 0,
  },
};
const errorText: { [index: string]: string } = {};

const initInputState: { [index: string]: EInputState } = {
  brokerUrl: EInputState.Undefined,
  databaseType: EInputState.Undefined,
  connectionString: EInputState.Undefined,
  limsInstrumentId: EInputState.Undefined,
  instrumentId: EInputState.Undefined,
  instrumentName: EInputState.Undefined,
  instrumentType: EInputState.Undefined,
  imagerType: EInputState.Undefined,
  canProductType: EInputState.Undefined,
  temperatureControlType: EInputState.Undefined,
  listenUrl: EInputState.Undefined,
  serverName: EInputState.Undefined,
  databaseName: EInputState.Undefined,
  username: EInputState.Undefined,
  password: EInputState.Undefined,
  useWindowsAuthentication: EInputState.Undefined,
  restApi: EInputState.Undefined,
  connectionType: EInputState.Undefined,
};

const getImagerTypeStrings = (
  instrumentType: EInstrumentType,
  instrumentsInfo: InstrumentsInfo
) => {
  const info = instrumentsInfo.instrumentsInfo.find((obj) => {
    return obj.instrumentType === instrumentType;
  });
  const imagerType = info!.imagerTypes.map((i) => EImagerType[i]);
  return imagerType;
};

function firstLower(lower: string) {
  return (lower && lower[0].toLowerCase() + lower.slice(1)) || lower;
}

type InitialConfigurationProps = {
  isNeedInitialConfiguration: boolean;
};

function InitialConfiguration(props: Readonly<InitialConfigurationProps>) {
  const { isNeedInitialConfiguration } = props;
  const dispatch = useDispatch();

  const [showEula, setShowEula] = useState(isNeedInitialConfiguration);
  const [isEditable, setIsEditable] = useState(false);
  const [isSuccessApply, setIsSuccessApply] = useState(false);

  const [initialConfiguration, setInitialConfiguration] =
    useState<SuiteInitialConfiguration>(initConfiguration);
  const [form, setForm] =
    useState<SuiteInitialConfiguration>(initConfiguration);
  const [inputState, setInputState] = useState<{
    [index: string]: EInputState;
  }>(initInputState);
  const [errorList, setErrorList] = useState(errorText);

  const [imagerTypeDataString, setImagerTypeDataString] = useState<string[]>(
    []
  );

  useEffect(() => {
    const fetchConfiguration = async () => {
      try {
        const [receivedInitialConfig, receivedInstrumentInfo] =
          await Promise.all([
            InitialConfigurationApi.getCurrentConfiguration(),
            InitialConfigurationApi.getInstrumentsInfo(),
          ]);

        setForm(receivedInitialConfig);
        setInitialConfiguration(receivedInitialConfig);
        const imagerTypeStrings = getImagerTypeStrings(
          receivedInitialConfig.instrumentType,
          receivedInstrumentInfo
        );
        setImagerTypeDataString(imagerTypeStrings);
      } catch (error) {
        dispatch(
          sendNotification(
            ENotificationType.Error,
            (error as any).message ?? "Can't connect to Suite"
          )
        );
      }
    };

    fetchConfiguration();
  }, [dispatch]);

  function setAllInputFieldStateTo(state: EInputState) {
    let newState: { [index: string]: EInputState } = {};
    for (let fieldName in inputState) {
      newState[firstLower(fieldName)] = state;
    }
    setInputState({ ...inputState, ...newState });
  }
  const handleOnResetForm = (isEditable: boolean) => {
    setIsEditable(isEditable);
    setForm(initialConfiguration);
    setAllInputFieldStateTo(EInputState.Undefined);
  };
  const handleOnCancel = () => handleOnResetForm(false);
  const handleOnSwitchEditMode = () => setIsEditable(true);
  const handleOnRestartService = () => WindowsServiceApi.restartService();
  const handleFormChange = (newConfig: SuiteInitialConfiguration) => {
    setForm(newConfig);
  };

  const handleOnApply = () => {
    setAllInputFieldStateTo(EInputState.Loading);
    InitialConfigurationApi.setCurrentConfiguration(false, form)
      .then(() => {
        setIsSuccessApply(true);
        setIsEditable(false);
        setAllInputFieldStateTo(EInputState.Success);
        showRestartConfirmation();
      })
      .catch((error) => {
        setAllInputFieldStateTo(EInputState.Undefined);
        if (error == "Initial configuration is not valid") {
          InitialConfigurationApi.getLastConfigurationError()
            .then((errorList) => {
              setErrorList(errorList);

              let newState: { [index: string]: EInputState } = {};
              //set Error
              for (let fieldName in errorList) {
                newState[firstLower(fieldName)] = EInputState.Error;
              }
              //set other as succsess
              for (let fieldName in inputState) {
                if (newState[fieldName] == undefined)
                  newState[fieldName] = EInputState.Success;
              }
              setInputState({ ...inputState, ...newState });
            })
            .catch((error) => {
              if (error.message != undefined) {
                dispatch(
                  sendNotification(ENotificationType.Error, error.message)
                );
              }
            });
        } else if (error.message != undefined) {
          dispatch(sendNotification(ENotificationType.Error, error.message));
        }
      });
  };

  const showRestartConfirmation = () => {
    dispatch(
      sendModalNotification(
        ENotificationType.Success,
        "System Initializing",
        "Configuration has been saved",
        `Please restart to continue the process`,
        () => {
          handleOnRestartService();
          dispatch(removeModalNotification());
          dispatch(
            sendNotification(
              ENotificationType.Success,
              "Please wait for the service to restart. Then, refresh the page.."
            )
          );
        },
        () => {
          dispatch(removeModalNotification());
        },
        "Restart Now",
        "Restart Later"
      )
    );
  };

  const isGeneralExceptionExists = errorList["General"] != null && errorList["General"] !== "";

  return (
    <LayoutContent showStatusAccordion={false} style={{ position: "absolute" }}>
      <LayoutContent.Header>
        <Header>
          <Header.RightSide>
            <MtiButton
              size="large"
              variant="secondary"
              style={{
                margin: "auto 10px auto 25px",
              }}
              disabled={!isEditable || isSuccessApply}
              onClick={handleOnCancel}
            >
              CANCEL
            </MtiButton>
            <MtiButton
              size="large"
              disabled={!isEditable || isSuccessApply}
              style={{
                margin: "auto 16px auto 10px",
              }}
              onClick={handleOnApply}
            >
              APPLY
            </MtiButton>
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.Content>
        <div className="initial-config-container">
          <div className="initial-config-content">
            <span className="initial-config-content-label">
              System Configuration
            </span>
            {isGeneralExceptionExists &&
              <div className="input-field-error-message">
                <ErrorIcon fill="#C92932" width="10" height="10" />
                <p>{errorList["General"]}</p>
              </div>}
            <InputField
              name="Broker URL"
              value={form.brokerUrl}
              onChange={(value) => {
                handleFormChange({
                  ...form,
                  brokerUrl: value as string,
                });
              }}
              variant="text"
              disabled={!isEditable}
              errorMessage={errorList["BrokerUrl"]}
              inputState={inputState["brokerUrl"]}
            />
            <InputField
              name="Listen URL"
              value={form.listenUrl}
              onChange={(value) =>
                handleFormChange({
                  ...form,
                  listenUrl: value as string,
                })
              }
              variant="text"
              disabled={!isEditable}
              errorMessage={errorList["ListenUrl"]}
              inputState={inputState["listenUrl"]}
            />

            <span className="initial-config-content-label">
              Database Settings
            </span>
            <SelectField
              name="Database Type"
              value={EDatabaseType[form.databaseType]}
              onChange={(value) => {
                const databaseTypeEnum: EDatabaseType =
                  EDatabaseType[value as keyof typeof EDatabaseType];
                handleFormChange({
                  ...form,
                  databaseType: databaseTypeEnum,
                });
              }}
              disabled={true}
              errorMessage={errorList["DatabaseType"]}
              inputState={inputState["databaseType"]}
              data={databaseTypeStrings}
            />
            <InputField
              name="Database Name"
              value={form.suiteDatabase.databaseName}
              onChange={(value) =>
                handleFormChange({
                  ...form,
                  suiteDatabase: {
                    ...form.suiteDatabase,
                    databaseName: value as string,
                  },
                })
              }
              variant="text"
              disabled={!isEditable}
              errorMessage={errorList["DatabaseName"]}
              inputState={inputState["databaseName"]}
            />
            <InputField
              name="Server Name"
              value={form.suiteDatabase.serverName}
              onChange={(value) =>
                handleFormChange({
                  ...form,
                  suiteDatabase: {
                    ...form.suiteDatabase,
                    serverName: value as string,
                  },
                })
              }
              variant="text"
              disabled={!isEditable}
              errorMessage={errorList["ServerName"]}
              inputState={inputState["serverName"]}
            />
            <InputField
              name="Username"
              value={form.suiteDatabase.userID}
              onChange={(value) =>
                handleFormChange({
                  ...form,
                  suiteDatabase: {
                    ...form.suiteDatabase,
                    userID: value as string,
                  },
                })
              }
              variant="text"
              disabled={!isEditable}
              errorMessage={errorList["Username"]}
              inputState={inputState["username"]}
            />
            <InputField
              name="Password"
              value={form.suiteDatabase.password}
              onChange={(value) =>
                handleFormChange({
                  ...form,
                  suiteDatabase: {
                    ...form.suiteDatabase,
                    password: value as string,
                  },
                })
              }
              variant="text"
              disabled={!isEditable}
              errorMessage={errorList["Password"]}
              inputState={inputState["password"]}
            />
            <SelectField
              name="Use Windows Authentication"
              value={
                UseWindowsAuthenticationType[
                form.suiteDatabase.useWindowsAuthentication
                  ? UseWindowsAuthenticationType.True
                  : UseWindowsAuthenticationType.False
                ]
              }
              onChange={(value) => {
                const valueEnum =
                  UseWindowsAuthenticationType[
                  value as keyof typeof UseWindowsAuthenticationType
                  ];
                handleFormChange({
                  ...form,
                  suiteDatabase: {
                    ...form.suiteDatabase,
                    useWindowsAuthentication: valueEnum as unknown as boolean,
                  },
                });
              }}
              disabled={!isEditable}
              errorMessage={errorList["UseWindowsAuthentication"]}
              inputState={inputState["useWindowsAuthentication"]}
              data={useWindowsAuthenticationTypeDropdown}
            />

            <span className="initial-config-content-label">
              Instrument Properties
            </span>

            <InputField
              name="Lims Instrument ID"
              value={form.limsInstrumentId}
              onChange={(value) =>
                handleFormChange({
                  ...form,
                  limsInstrumentId: value as number,
                })
              }
              variant="text"
              disabled={!isEditable}
              errorMessage={errorList["LimsInstrumentId"]}
              inputState={inputState["limsInstrumentId"]}
            />
            <InputField
              name="Instrument ID"
              value={form.instrumentId}
              onChange={(value) =>
                handleFormChange({
                  ...form,
                  instrumentId: value as string,
                })
              }
              variant="text"
              disabled={!isEditable}
              errorMessage={errorList["InstrumentId"]}
              inputState={inputState["instrumentId"]}
            />

            <InputField
              name="Instrument Name"
              value={form.instrumentName}
              onChange={(value) =>
                handleFormChange({
                  ...form,
                  instrumentName: value as string,
                })
              }
              variant="text"
              disabled={!isEditable}
              errorMessage={errorList["InstrumentName"]}
              inputState={inputState["instrumentName"]}
            />
            <SelectField
              name="Instrument Type"
              value={EInstrumentType[form.instrumentType]}
              onChange={(value) => {
                const instrumentDataEnum: EInstrumentType =
                  EInstrumentType[value as keyof typeof EInstrumentType];
                handleFormChange({
                  ...form,
                  instrumentType: instrumentDataEnum,
                });
              }}
              disabled={!isEditable}
              errorMessage={errorList["InstrumentType"]}
              inputState={inputState["instrumentType"]}
              data={instrumentTypeStrings}
            />
            <SelectField
              name="Imager Variant"
              value={EImagerType[form.imagerType]}
              onChange={(value) => {
                const imagerTypeEnum: EImagerType =
                  EImagerType[value as keyof typeof EImagerType];
                handleFormChange({
                  ...form,
                  imagerType: imagerTypeEnum,
                });
              }}
              disabled={!isEditable}
              errorMessage={errorList["ImagerType"]}
              inputState={inputState["imagerType"]}
              data={imagerTypeDataString}
            />
            <SelectField
              name="Can Product Type"
              value={ECanProduct[form.canProductType]}
              onChange={(value) => {
                const valueEnum =
                  ECanProduct[value as keyof typeof ECanProduct];
                handleFormChange({
                  ...form,
                  canProductType: valueEnum,
                });
              }}
              disabled={!isEditable}
              errorMessage={errorList["CanProductType"]}
              inputState={inputState["canProductType"]}
              data={canProductDropdown}
            />
            <SelectField
              name="Temperature Control Type"
              value={ETemperatureControlType[form.temperatureControlType]}
              onChange={(value) => {
                const valueEnum =
                  ETemperatureControlType[
                  value as keyof typeof ETemperatureControlType
                  ];
                handleFormChange({
                  ...form,
                  temperatureControlType: valueEnum,
                });
              }}
              disabled={!isEditable}
              errorMessage={errorList["TemperatureControlType"]}
              inputState={inputState["temperatureControlType"]}
              data={temperatureControlTypeDropdown}
            />

            <span className="initial-config-content-label">
              Integration Properties
            </span>

            <SelectField
              name="Connection Type"
              value={EIntegrationConnectionType[form.connectionType]}
              onChange={(value) => {
                const connectionTypeEnum: EIntegrationConnectionType =
                  EIntegrationConnectionType[value as keyof typeof EIntegrationConnectionType];
                handleFormChange({
                  ...form,
                  connectionType: connectionTypeEnum,
                });
              }}
              disabled={!isEditable}
              errorMessage={errorList["ConnectionType"]}
              inputState={inputState["connectionType"]}
              data={integrationConnectionTypeDropdown}
            />
            {form.connectionType === EIntegrationConnectionType.RestApi && (
              <>
                <InputField
                  name="Api URL"
                  value={form?.restApi?.apiUrl}
                  onChange={(value) =>
                    handleFormChange({
                      ...form,
                      restApi: {
                        ...form.restApi,
                        apiUrl: value as string,
                      },
                    })
                  }
                  variant="text"
                  disabled={!isEditable}
                  errorMessage={errorList["RestApi"]}
                  inputState={inputState["restApi"]}
                />

                <InputField
                  name="Username"
                  value={form.restApi?.username}
                  onChange={(value) =>
                    handleFormChange({
                      ...form,
                      restApi: {
                        ...form.restApi,
                        username: value as string
                      },
                    })
                  }
                  variant="text"
                  disabled={!isEditable}
                  errorMessage={errorList["RestApi"]}
                  inputState={inputState["restApi"]}
                />
                <InputField
                  name="Password"
                  value={form.restApi?.password}
                  onChange={(value) =>
                    handleFormChange({
                      ...form,
                      restApi: {
                        ...form.restApi,
                        password: value as string
                      },
                    })
                  }
                  variant="password"
                  disabled={!isEditable}
                  errorMessage={errorList["RestApi"]}
                  inputState={inputState["restApi"]}
                />
                <InputField
                  name="Time Out"
                  value={form.restApi?.timeOutInSeconds}
                  onChange={(value) =>
                    handleFormChange({
                      ...form,
                      restApi: {
                        ...form.restApi,
                        timeOutInSeconds: value as number
                      },
                    })
                  }
                  variant="text"
                  disabled={!isEditable}
                  errorMessage={errorList["RestApi"]}
                  inputState={inputState["restApi"]}
                />
              </>
            )}

          </div>
          <div className="initial-config-footer">
            <MtiButton
              size="large"
              variant="primary"
              colorVariant="black"
              disabled={!isEditable}
              onClick={() => {
                handleOnResetForm(true);
              }}
            >
              <IconReset width={20} height={16} />
              <span>Reset</span>
            </MtiButton>
            <MtiButton
              size="large"
              variant="primary"
              disabled={isEditable || isSuccessApply}
              onClick={handleOnSwitchEditMode}
            >
              <IconEdit />
              <span>Edit</span>
            </MtiButton>
          </div>
        </div>
        <EulaModal show={showEula} onClickOk={() => setShowEula(false)} />
      </LayoutContent.Content>
    </LayoutContent>
  );
}

export default InitialConfiguration;
